<script setup lang="ts">
import { useOrderStore } from '../stores/order'
import type { CanvasSchemaData } from '@/types'
import type { IEvent } from '@/types'
import { ORDER_LIMIT } from '../utils/constants'
import { default as currency } from 'currency.js'

const props = defineProps<{
  event: IEvent<CanvasSchemaData>
  loading?: boolean
  createOrder: Function
  commission: number
  settings: {
    percentage: {
      offline: { value: number }
      online: { value: number }
    }
    features: {
      offlineSales: boolean
      onlineSales: boolean
      externalPercentage: boolean
    }
  }
}>()

const { event } = toRefs(props)

const orderStore = useOrderStore()

const { moneyFormater } = useMoneyFormater()

const cartAmount = computed(() => orderStore.cartAmount)
const commissionAmount = computed(() =>
  Number(currency(Number(cartAmount.value)).divide(100).multiply(props.commission).toString())
)
const orderAmount = computed(() =>
  Number(
    props.settings.features.externalPercentage
      ? currency(cartAmount.value).add(commissionAmount.value).toString()
      : cartAmount.value
  )
)

const cart = computed<CanvasSchemaData[]>({
  get: () => {
    return orderStore.cart as CanvasSchemaData[]
  },
  set: (value: any[]) => {
    orderStore.setCart(value)
  }
})

const schema = computed(() => {
  return Object.values(event.value.tariff.schema)
})

const isLimitOrder = ref(false)

function onClickOnObject(e: Event) {
  console.log(e)
  const params = e.detail.getParams() as {
    active: boolean
    avail: boolean
    backgroundColor: string
    cursorStyle: string
    fontColor: string
    fontSize: number
    height: number
    id: string
    isSelected: boolean
    price: string
    rotation: number
    row: number
    seat: number
    sectorName: string
    width: number
    x: number
    y: number
  }

  if (!params.avail || !params.active) {
    return
  }

  const ticket = schema.value.find((ticket) => ticket.ID === params.id) as CanvasSchemaData

  if (cart.value.find((ticket) => ticket.ID === params.id)) {
    orderStore.removeFromCart(ticket)
    isLimitOrder.value = false
  } else {
    if (orderStore.cart.length < ORDER_LIMIT) {
      orderStore.addToCart(ticket)
    } else {
      isLimitOrder.value = true
      setTimeout(() => {
        isLimitOrder.value = false
      }, 2000)
    }
  }
}

const { canvas, zoomIn, zoomOut, render } = useSchemaRenderer(schema, {
  onClickOnObject
})

onMounted(() => {
  render()
})

const groupByColor = computed(() => {
  return [
    // @ts-ignore
    ...schema.value
      .filter((val) => val.avail === true)
      .reduce<Map<string, number>>(
        // @ts-ignore
        (res: Map<string, number>, el: { BackColor: string; Price: number }): Map<string, number> =>
          res.set(el.BackColor, Number(el.Price) as number),
        new Map<string, number>()
      )
      .entries()
  ]
    .sort(([_1, p1], [_2, p2]) => p1 - p2)
    .filter(([_, price]) => Number(price) > 0)
})
</script>

<template>
  <header
    class="absolute top-0 pt-5 pb-10 h-25 left-0 right-0 bg-white"
    style="box-shadow: 0px 24px 20px white"
  >
    <div class="container flex flex-row gap-4 px-5 pt-2 flex-wrap">
      <template v-for="[color, price] in groupByColor">
        <div v-if="!(price === -1 || price === '-1')" :key="color" class="px-2 flex flex-row gap-2">
          <div class="rounded h-5 w-5 my-auto" :style="{ backgroundColor: `#${color}` }" />
          <p class="my-auto font-600">
            {{ moneyFormater(price) }}
          </p>
        </div>
      </template>
    </div>
  </header>
  <main class="mx-auto h-full w-full">
    <canvas id="canvas" ref="canvas" class="w-full h-full" />
    <canvas
      id="canvas"
      ref="mapCanvas"
      class="w-0 h-0 lg:w-30 lg:h-30 absolute top-5 right-5 z-50"
    />
    <button
      type="button"
      class="bg-gray-400 h-8 w-8 absolute right-4 rounded flex items-center justify-center"
      style="top: calc(50% - 40px)"
      @click="zoomIn"
    >
      <t-icon-plus class="w-7 h-7" color="#000000" />
    </button>
    <button
      type="button"
      class="bg-gray-400 h-8 w-8 absolute right-4 rounded flex items-center justify-center"
      style="top: calc(50% + 2px)"
      @click="zoomOut"
    >
      <t-icon-minus class="w-5 h-5" color="#000000" />
    </button>
    <section v-if="isLimitOrder">
      <div
        class="absolute top-8 left-2/4 right-2/4 xl:top-auto xl:bottom-40 xl:left-auto xl:right-10 bg-black text-white -translate-x-2/4 xl:translate-x-0 rounded-xl px-4 py-4 right-0 w-full max-h-21 max-w-90"
      >
        <w-icon-close
          class="absolute w-4 h-4 top-4 right-4 cursor-pointer"
          @click="isLimitOrder = false"
        ></w-icon-close>
        <p style="color: #e9e7e4">За один заказ можно купить не более {{ ORDER_LIMIT }} билетов</p>
      </div>
    </section>
    <section
      v-if="cart.length"
      class="absolute pb-4 xl:pb-0 bottom-0 lg:bottom-7 h-auto left-0 right-0 bg-white text-slate-700 px-4 lg:px-16 border-t-2 border-gray-400"
    >
      <div class="block md:hidden">
        <div class="pt-4 pb-2 flex flex-row space-x-2 text-xl font-semibold font-sf-pro">
          <p class="whitespace-nowrap">
            {{ orderStore.cart.length }}
            {{
              orderStore.cart.length === 1
                ? 'билет'
                : orderStore.cart.length < 5
                ? 'билета'
                : 'билетов'
            }}:
          </p>
          <p>
            {{ moneyFormater(orderAmount) }}
          </p>
          <p class="text-sm text-gray-900 font-sf-pro" v-if="settings.features.externalPercentage">
            Размер комиссии
            {{ moneyFormater(commissionAmount) }}
          </p>
        </div>
      </div>
      <div class="h-full flex flex-row py-4" id="scrollBlock">
        <div
          v-for="(ticket, index) in cart"
          :key="index"
          class="relative flex flex-col justify-center text-left font-sans text-base gap-1 pl-4 pr-4 border-r-1 border-r-gray-400 min-w-35"
          :class="[index === 0 ? 'border-l-1 border-r-gray-400' : '']"
        >
          <h6 class="text-xs whitespace-nowrap">
            {{ `${ticket.Row ? `${ticket.Row} ряд,` : ''} ${ticket.Seat} место` }}
          </h6>
          <p v-if="ticket.Name_sec" class="text-xs text-gray-300">
            {{ ticket.Name_sec }}
          </p>
          <p class="text-lg font-600">
            {{ moneyFormater(ticket.Price) }}
          </p>
        </div>
        <div v-if="cart.length > 0" class="hidden md:flex ml-auto gap-8">
          <div class="my-auto ml-4 font-600 text-xl">
            <p class="whitespace-nowrap">
              {{ orderStore.cart.length }}
              {{
                orderStore.cart.length === 1
                  ? 'билет'
                  : orderStore.cart.length < 5
                  ? 'билета'
                  : 'билетов'
              }}
            </p>
            <p>
              {{ moneyFormater(orderAmount) }}
            </p>
            <p
              class="text-sm text-gray-900 font-sf-pro"
              v-if="settings.features.externalPercentage"
            >
              Размер комиссии
              {{ moneyFormater(commissionAmount) }}
            </p>
          </div>
          <TButton
            class="my-auto"
            rounded="large"
            :loading="isPendingOrderCreation"
            @click="createOrder"
          >
            Далее
          </TButton>
        </div>
      </div>
      <div class="w-full pt-3 block md:hidden">
        <TButton
          class="my-auto"
          rounded="large"
          :loading="isPendingOrderCreation"
          @click="createOrder"
        >
          Далее
        </TButton>
      </div>
    </section>
  </main>
</template>

<style scoped>
#scrollBlock {
  overflow-x: scroll;
  max-width: 100%;
}

#scrollBlock::-webkit-scrollbar {
  height: 2px;
  width: 7px !important;
  border-radius: 9px !important;
}

#scrollBlock::-webkit-scrollbar-track {
  background-color: #fefefe;
}

#scrollBlock::-webkit-scrollbar-thumb {
  width: 7px;
  border-radius: 9px;
  background-color: #0575dd;
}
</style>
